import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DeserializeContext } from "abweb/utils/json/DeserializeContext";
import { SerializeContext } from "abweb/utils/json/SerializeContext";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ModuleLoaderUtils } from "abweb/utils/ModuleLoaderUtils";

// export type ObjectMapType = { [objId: number]: Object };

/**
 * Typescript Object Graph support -  deserializing JSOG (json object graph) to Typescript class instances.
 */
export class TSOGUtils {
  ////////////////////   Deserialization from server-emitted JSON to TS Objects support //////////////////

  /**
   * The following are inverses of one another:
   * tsogString = TSOGUtils.stringify(mything);
   * mything = TSOGUtils.parse(tsogString, MyThingClass);
   */
  static parse(rawJson, fnCtor, deserializeContext = new DeserializeContext()) {
    // Note that JSON.parse barfs on empty string, so short-circuit on null or empty string or undefined
    if (!rawJson) {
      return null;
    }
    const json = JSON.parse(rawJson);
    return TSOGUtils.decodeObject(json, fnCtor, deserializeContext);
  }

  /**
   * @param json raw json object
   * @param defaultConstructor default constructor for json object (json can contain @class prop which specifies actual constructor)
   * @param objMap map of objects that have already been created as part of the entire decode stream
   * @param targetLVal optional context used to set the object back into the object graph.  The ONLY time a targetLVal
   * does not need to be provided is if it the top-level object being returned from an rpc (and as such, the object
   * has to be the in-place object definition, and NOT a numeric reference id).
   * @return {any} appropriately decoded typescript object or primitive (or array of, or map of, or ...)
   */
  static decodeObject(json, defaultConstructor, deserializeContext = new DeserializeContext(), targetLVal) {
    if (json == null) {
      return null;
    }
    // Support cyclic object graphs: ref to previously defined object, such as "objName": 38
    if (typeof json === "number") {
      const objId = json;
      const refObj = deserializeContext.getObject(objId);
      if (refObj) {
        return refObj;
      } else if (targetLVal) {
        deserializeContext.addReference(objId, targetLVal);
        return null; // will be fixed up to actual value later when obj with objId is deserialized
      } else {
        const errMsg = `Unable to resolve objId=${objId} while deserializing, and target obj[propName] is not available.`;
        TSOGUtils.LOG.error(errMsg);
        throw new Error(errMsg);
      }
    }

    // Support polymorphism by having specific constructors (subclass constructor)
    // overwrite the provided default constructor (base constructor).
    // Note that only non-final classes will have the @class annotation, so @class will NOT always be present
    const classname = json["@class"];
    let ctor = defaultConstructor;
    if (classname !== undefined) {
      // Defer: might want to do runtime check that ctor is for a subtype of T...
      // Throws if classname not found in registry
      ctor = TSOGUtils.getClassConstructor(classname);
    } else if (ctor == null) {
      throw new Error("Unable to decode the following json as it does not contain @class property " + "nor was a default constructor supplied (might be an interface or abstract class).\n\t" + json); // no guarantee that this will be pretty, but at least it is something...
    }

    // Now create the empty object of the given (sub) type, conditionally register it, then finish initializing it
    const me = new ctor();

    // Note that some generated TS classes may not have JSOGGeneration annotation, so conditionally check for id
    // Need to register after empty constructor but before initFromJson in order to handle cyclic references
    const id = json["@id"];
    if (id != null) {
      deserializeContext.registerObject(id, me);
      me[TSOGUtils.TSOG_ID_PROP] = TSOGUtils.genId(); // Needed only for Cristhian's Runtime Cyclic Detection algorithm
    }
    me.initFromJson(json, deserializeContext);
    return me;
  }
  static parseArray(rawJson, fnCtor, deserializeContext = new DeserializeContext()) {
    // Note that JSON.parse barfs on empty string, so short-circuit on null or empty string or undefined
    if (!rawJson) {
      return null;
    }
    const json = JSON.parse(rawJson);
    return TSOGUtils.decodeObjectArray(json, fnCtor, deserializeContext);
  }

  /*
   * Use to decode json array of Foo[], where Foo is not a primitive type (it is an Object instance).
   * The array must directly contain the objects
   * (e.g., it can not be an array containing an array (or map) that contains the objects)
   */
  static decodeObjectArray(json, arrayTypeCtor, deserializeContext = new DeserializeContext(), arrayHolder) {
    if (json == null) {
      return null;
    }
    // In-place modification which is (slightly) more memory efficient.
    // Also needed for lazy dereferencing when ref is encountered before obj def (CAT-500) so that lval is correct
    json.forEach((item, index) => json[index] = TSOGUtils.decodeObject(item, arrayTypeCtor, deserializeContext, DeserializeContext.createContainerReferenceContext(arrayHolder, index, json)));
    return json;
  }

  /*
   * Use to decode json array of Foo[], where Foo is an enum.
   * The array must directly contain the enums
   * (e.g., it can not be an array containing an array (or map) that contains the enums)
   */
  static decodeEnumArray(json, T)
  // public static decodeEnumArray<T>(json: any, enumFromJson: (enumVal: any) => T): T[]
  {
    if (json == null) {
      return null;
    }
    // In-place modification which is (slightly) more memory efficient.
    // Also needed for lazy dereferencing when ref is encountered before obj def (CAT-500) so that lval is correct
    json.forEach((item, index) => json[index] = T.fromJson(item));
    return json;
  }

  /*
   * Use to decode an enum directly
   */
  static decodeEnum(json, T)
  // public static decodeEnumArray<T>(json: any, enumFromJson: (enumVal: any) => T): T[]
  {
    if (json == null) {
      return null;
    }
    return T.fromJson(json);
  }

  /*
   * Use to decode json map of <string, Foo>, where Foo is not a primitive type (it is an Object instance).
   * The map must directly contain the objects (e.g., it can not be a map whose values are anything other than Foo instances)
   */
  static decodeObjectMap(json, valueTypeCtor, deserializeContext = new DeserializeContext(), mapHolder) {
    // json.myRecursiveMap != null
    // ? <below>
    // : null!
    if (json == null) {
      return null;
    }
    for (const key in json) {
      json[key] = TSOGUtils.decodeObject(json[key], valueTypeCtor, deserializeContext, DeserializeContext.createContainerReferenceContext(mapHolder, key, json));
    }
    return json;
  }

  /*
   * Use to decode json array of Foo[], where Foo is an enum.
   * The array must directly contain the enums
   * (e.g., it can not be an array containing an array (or map) that contains the enums)
   */
  static decodeEnumMap(json, T) {
    if (json == null) {
      return null;
    }

    // in-place mutation of map
    for (const key in json) {
      json[key] = T.fromJson(json[key]);
    }
    return json;
  }

  /*
   NOT needed as ts compiler doesn't complain if you do (surprising as the key types are different):
   let myMap: {[id: number]: Foo} = TSOGUtils.decodeObjectMap(json, Foo, {})
   public static decodeNumberObjectMap<ValT extends IDeserialize>(json: any, valueTypeCtor: new () => ValT,
   objMap: idMapType = {}): {[id: number]: ValT}
   {
   // simple ts syntactic sugar for correct return type, as decodeObjectMap passes through the keyT (which is json primitive)
   return TSOGUtils.decodeObjectMap(json, valueTypeCtor, objMap);
   }
   */

  /**
   * Register typescript class to instantiate for an incoming json stream's @class attribute value (classname).
   * The classname is typically the full java classname, but if serializing/deserializing solely on the client
   * side then it just needs to uniquely reference a typescript class.
   * @param classname
   * @param fnCtor
   */
  static registerClass(classname, fnCtor) {
    // TSOGUtils.LOG.debug("registerClass classname: " + classname + ", fnCtor: " + fnCtor.name);
    TSOGUtils.classConstructorMap[classname] = fnCtor;
  }
  static async registerClasses(tsogClassRegistrationSpec) {
    // When the below module is loaded, it has static imports that SystemJS walks to transitively import the tsog classes.
    // This is profiled via the built-in ModuleLoaderUtils.import profiling
    const tsogRegistrationClass = await ModuleLoaderUtils.import(tsogClassRegistrationSpec);

    // This always took 1msec (just adding entries into a map), so uninteresting to profile
    tsogRegistrationClass.registerClasses();
  }

  /**
   * Returns class constructor or throws error
   * @param classname
   * @return {{new()=>any}}
   */
  static getClassConstructor(classname) {
    const fnCtor = TSOGUtils.classConstructorMap[classname];
    if (fnCtor === undefined) {
      throw new Error("There is no registered Typescript class for java class: " + classname);
    }
    return fnCtor;
  }

  ////////////////////   Serialization from TS Objects to JSON support //////////////////

  /**
   * Serialization to JSON string
   * The following are inverses of one another:
   * tsogString = TSOGUtils.stringify(mything);
   * mything = TSOGUtils.parse(tsogString, MyThingClass);
   * <p>
   * <ol>Key observation is that JSON.stringify is fairly close to what we need,
   * EXCEPT that it:
   * <ol>
   * <li>does not handle cycles</li>
   * <li>does not emit @class attribute for server-side jackson polymorphism support</li>
   * <li>emits 'undefined' for undefined values, which is NOT supported by json spec</li>
   * </ol>
   *
   * Therefore pre-walk object graph removing cycles via @id and objMap, adds @class annotation as needed,
   * and converts undefined values in object graph to null (CAT-945).
   * <p>
   * Future enhancement: could speed up via specialized 'toJSON' methods on (generated) classes, but current approach
   * is probably fast enough as volume of data sent from client to server is generally MUCH smaller than inverse.
   * <p>
   *     Implementation is based on adapting JSOG approach outlined in
   *     <pre>https://github.com/jsog/jsog/blob/master/src/JSOG.coffee
   *     </pre>
   * to Typescript and Jackson format (@JsonIdentityInfo @id format is @number and @class support)
   * <p>
   *
   * @param thing
   * @return {string}
   */
  static stringify(thing) {
    return JSON.stringify(TSOGUtils.toTSOGAny(thing, new SerializeContext()));
  }

  /**
   * Walk the object graph, and for every obj assign an @id so that can replace future refs in obj stream.
   * Also while walking the object graph (note array and map walking invoke toTSOGAny for each value),
   * convert undefined to null as the json spec does not support undefined
   * (yet JSON.stringify(undefined) returns 'undefined', which isn't value according to json spec - sigh...)
   *
   * <p>CAT-945: undefined in not part of json spec, so also converts undefined vals in obj graph to null as part
   * of object graph walk
   */
  static toTSOGAny(thing, serializeContext) {
    // This is responsible for removing cycles, regardless of whether the subsequent toJSON will be customized by a
    // class or not (so do NOT short-circuit on obj.toJSON, which KendoObservableObject defines!)
    let retVal;
    if (thing == null)
      // null or undefined
      {
        retVal = null; // CAT-945: Coalesce undefined to null for serialization, as json spec does not support undefined
      } else if (ArrayUtils.isArrayLike(thing))
      // has to be first as 'typeof myArr' returns 'object' as well
      {
        retVal = TSOGUtils.toTSOGArray(thing, serializeContext); // never returns undefined
      } else if (typeof thing === "object") {
      if (thing["toTSOG"] != null)
        // instanceof ISerialize
        {
          retVal = TSOGUtils.toTSOGNativeObject(thing, serializeContext); // never returns undefined
        }
        //let javaClassFn: () => string = (<any> obj).__proto__.constructor[TSOGUtils.TSOG_JAVA_CLASS_FN];
        //if (javaClassFn !== undefined)
        //{
        //  return TSOGUtils.toTSOGNativeObject(obj, serializeContext, javaClassFn());
        //}
      else if (ClassUtils.isInstanceOf(thing, Date)) {
        // JSON stringify natively handles Date, so return directly
        retVal = thing;
      } else if (ClassUtils.isInstanceOf(thing, Map)) {
        // CAT-1226: handle es6 Map instances as an rpc argument (but do NOT handle es6 Map as return value from rpc)
        retVal = TSOGUtils.es6MapToJson(thing, serializeContext); // never returns undefined
      } else {
        retVal = TSOGUtils.toTSOGMap(thing, serializeContext); // never returns undefined
      }
    } else {
      retVal = thing; // primitive...
    }

    // CAT-945: Coalesce undefined to null for serialization, as json spec does not support undefined
    // Only code path above that could return undefined (when incoming 'thing' is undefined) is handled
    // by thing==null code path above (converts to null)
    return retVal;
  }

  // obj could be a 'native' tsog class, arbitrary class, Date, or 'map'
  static toTSOGArray(objArray, serializeContext) {
    // if the array type is non-nullable, then a null should never be passed in to this function.
    // But occasionally the runtime values do have a null for a non-nullable array, and want to be
    // robust (do not want serialization to blow up with NPE)
    return objArray != null ? objArray.map(item => TSOGUtils.toTSOGAny(item, serializeContext)) : null;
  }
  static toTSOGMap(obj, serializeContext) {
    // Else iterate through each keys (aka props) of Map or (non-TSOG) Object
    // If a javascript map (implicit or explicit) is serialized with an @id, jackson deserialization barfs.
    //    Example: public mapStringToObj: {[id: string]: ConfigValue};
    // Quick Current Fix: don't add @id to maps (only add to objects that have getJavaClass static method defined, aka tsog objects above)
    // Downside: won't handle cycles involving maps (but these should be rare)
    // Longer-term Fix: fix jackson map deserialization to handle @id gracefully
    const result = {};
    for (const key in obj) {
      // Do not care if it has a __TSOG_ID prop, as those only have special semantics for tsog objects
      result[key] = TSOGUtils.toTSOGAny(obj[key], serializeContext);
    }
    return result;
  }

  // Map is really Map<string|number|boolean, any>, will throw if any other key type encountered (including undefined)
  static es6MapToJson(m, serializeContext) {
    let firstKey = true;
    const obj = Object.create(null); // {k1: v1}
    for (let [k, v] of m) {
      // Defensive: As Map is strongly typed, if the first row has an acceptable key type, then all entries will
      // (ignoring case where dev bypasses typescript type checking via 'any' cast). So only check first row.
      if (firstKey) {
        firstKey = false;
        const keyType = typeof k;
        if (keyType !== "string" && keyType !== "number" && keyType !== "boolean") {
          throw new Error("es6MapToJson conversion of Map only supports key types of string|number|boolean, but encountered key type: '" + keyType + "'");
        }
      }
      // Convert undefined to null as json spec does not support undefined (Possibly dangerous if already has another 'null' key)
      if (k === undefined) {
        // k = null!;  can't do this as might clobber another entry that has null as the key value (lossy conversion).
        // And even if m doesn't currently have both null and undefined keys, it someday might, so defensively throw.
        throw new Error("es6MapToJson can not serialize maps that have 'undefined' as a key value");
      }
      obj[k] = TSOGUtils.toTSOGAny(v, serializeContext);
    }
    return obj;
  }

  // Could 'copy' the below logic into every generated 'root' class, but prefer single source of the logic (this method)
  static toTSOGNativeObject(tsogObj, serializeContext) {
    // Unfortunately, if they have a type of Foo, and it should have been Foo | null, then this will still be invoked
    // at runtime with tsogObj = null.  Safely handle it.
    if (tsogObj == null) {
      return null;
    }
    const id = TSOGUtils.getOrCreateId(tsogObj);
    if (serializeContext.addObjectId(id)) {
      return id; // id already existed
    }
    // Putting @id and @class first makes debugging easier as outputs first.  Thus pass it in as the pre-populated arg.
    // Could also append efficiently via result["@id"]=id, but this puts id/class at end
    // which isn't as useful for debugging.
    // Or use jquery.extend to prepend @id and @class to returned toTSOG result, but this is inefficient
    // (and web site 'jsperf.com' is currently being rewritten, which is where all google performance searches go... sigh)
    return tsogObj.toTSOG({
      "@id": id,
      "@class": tsogObj.getJavaClass()
    }, serializeContext);
  }

  // For now, only use with TSOG generated (native) classes.
  // Formerly used for arbitrary maps "{}" and arrays as well to ensure no circular dependencies,
  // but jackson barfed (see comment in toTSOGMap).
  static getOrCreateId(obj) {
    let id = obj[TSOGUtils.TSOG_ID_PROP];
    return id === undefined ? obj[TSOGUtils.TSOG_ID_PROP] = TSOGUtils.genId() // assignment returns a value!
    : id;
  }
  static getIdFromObject(obj) {
    return obj[TSOGUtils.TSOG_ID_PROP];
  }
  static genId() {
    return TSOGUtils.nextId++;
  }
  static resetIdCounterForTests() {
    TSOGUtils.nextId = 1;
  }

  /**
   * @param {T extends IDeserialize | null} obj Object to be cloned
   * @param {{new(): T}} fnCtor The class of the object
   * @return {T | null} a deep clone of the object for the TSOG properties only (does NOT clone any client-side only
   * properties).
   */
  static clone(obj, fnCtor) {
    if (obj == null) {
      return null;
    }
    // Note this is a deep clone as it goes all the way to string and back.  If I only went to the json object level and
    // back, then it would be a shallow clone in the sense that maps and arrays that solely contain primitives would be
    // shared between the clone and the original obj.
    const json = TSOGUtils.stringify(obj);
    return TSOGUtils.parse(json, fnCtor);
  }
}
_defineProperty(TSOGUtils, "LOG", LogUtils.getLogger(__filename));
_defineProperty(TSOGUtils, "classConstructorMap", {});
_defineProperty(TSOGUtils, "TSOG_ID_PROP", "__TSOG_ID");
_defineProperty(TSOGUtils, "nextId", 1);
//# sourceMappingURL=TSOGUtils.js.map
